import React from 'react';
import { useReportVisibility } from '../contexts/ReportVisibilityContext';

/**
 * VisibilityGate — wraps any report element with admin-controlled visibility.
 * 
 * For admin: shows a small inline checkbox to toggle visibility.
 * For regular users: simply hides the element if visibility[sectionKey] is false.
 * 
 * @param {string} sectionKey - unique key like "gis.growth_direction"
 * @param {string} label - human-readable label shown next to admin checkbox
 * @param {React.ReactNode} children - the content to gate
 * @param {string} className - optional additional classes
 */
const VisibilityGate = ({ sectionKey, label, children, className = '' }) => {
  const { visibility, isAdmin, toggleVisibility } = useReportVisibility();
  const isVisible = visibility[sectionKey] !== false; // default true if key not present

  // Non-admin: simply hide if not visible
  if (!isAdmin && !isVisible) return null;

  return (
    <div className={`relative group/vg ${className}`} data-testid={`vg-${sectionKey}`}>
      {/* Admin checkbox overlay */}
      {isAdmin && (
        <div
          className={`absolute -left-6 top-1 z-10 opacity-0 group-hover/vg:opacity-100 transition-opacity duration-150`}
          data-testid={`vg-toggle-${sectionKey}`}
        >
          <label className="flex items-center cursor-pointer" title={`Toggle: ${label}`}>
            <input
              type="checkbox"
              checked={isVisible}
              onChange={() => toggleVisibility(sectionKey)}
              className="h-3.5 w-3.5 rounded border-slate-400 text-purple-600 focus:ring-purple-500 cursor-pointer"
            />
          </label>
        </div>
      )}
      {/* Content — dimmed when admin has it unchecked */}
      {isAdmin && !isVisible ? (
        <div className="opacity-30 pointer-events-none border border-dashed border-red-300 rounded px-1" title={`Hidden: ${label}`}>
          {children}
        </div>
      ) : (
        children
      )}
    </div>
  );
};

export default VisibilityGate;
